// @ts-check
/**
 * projectMoney.js - derived money figures for a project/opportunity row.
 *
 * Leftover used to be a hand-typed column, so it drifted from reality (people
 * updated the invoiced amount and forgot the leftover). It is now DERIVED:
 *
 *     leftover = PO value - invoiced
 *
 * and shows BLANK only when the job is fully invoiced (invoiced === PO, to the
 * cent). One
 * helper, used by every consumer (detail panels, the table cell, sorting,
 * totals, CSV export) so the number can never disagree with itself.
 */

/**
 * The money columns of a projects row, as the API sends it (snake_case) or
 * the client holds it (camelCase). Values may be numbers or numeric strings.
 * @typedef {{
 *   poValue?: number | string | null, po_value?: number | string | null,
 *   invoicedAlready?: number | string | null, invoiced_already?: number | string | null,
 *   moneyLeftover?: number | string | null, money_leftover?: number | string | null,
 *   quotedPrice?: number | string | null, quotedLaborCost?: number | string | null,
 *   quotedExpenses?: number | string | null
 * }} MoneyRow
 */

/** @param {unknown} v @returns {number | null} */

const num = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : parseFloat(String(v).replace(/[$,\s]/g, ''));
  return Number.isFinite(n) ? n : null;
};

/** PO value on a row, snake_case or camelCase. @param {MoneyRow | null | undefined} p */
export const poValueOf = (p) => num(p?.poValue ?? p?.po_value);

/** Invoiced-to-date on a row. @param {MoneyRow | null | undefined} p */
export const invoicedOf = (p) => num(p?.invoicedAlready ?? p?.invoiced_already);

/**
 * leftoverOf(project) -> number | null
 *   null  = show nothing (fully invoiced, or nothing to compute from)
 *   number = PO minus invoiced (negative means over-invoiced - worth seeing)
 * With a PO present, a missing invoiced amount counts as 0 (nothing billed
 * yet), so leftover equals the whole PO. With no PO we fall back to any stored
 * value so legacy rows that predate PO tracking still display.
 * @param {MoneyRow | null | undefined} p
 * @returns {number | null}
 */
export function leftoverOf(p) {
  const po = poValueOf(p);
  if (po !== null && po !== 0) {
    // To the cent: imported figures carry float noise (PO 62674.5 vs invoiced
    // 62674.50000000001), which is not an over-invoice and used to print as a
    // red "-$0.00". Math.round gives -0 there, and -0 === 0, so it reads blank.
    const diff = Math.round((po - (invoicedOf(p) ?? 0)) * 100) / 100;
    return diff === 0 ? null : diff;
  }
  return num(p?.moneyLeftover ?? p?.money_leftover);
}

/** Sort/aggregate-safe variant: blank counts as 0. @param {MoneyRow | null | undefined} p @returns {number} */
export const leftoverNum = (p) => leftoverOf(p) ?? 0;

/**
 * Quoted gross margin as a 0-1 fraction, or null when there is no price.
 * @param {MoneyRow} p
 * @returns {number | null}
 */
export function computeMargin(p) {
  const price = Number(p.quotedPrice) || 0;
  if (!price) return null;
  const labor = Number(p.quotedLaborCost) || 0;
  const exp = Number(p.quotedExpenses) || 0;
  return (price - (labor + exp)) / price;
}

/**
 * Add one invoice to a project's invoicing fields (Sharif, 10/8/26: "every
 * time I get invoiced I have to add the new amount to the total myself").
 * `fields` = { invoiceNumbers, invoicedAlready, latestInvoiceDate } as the
 * form holds them; `inv` = { number, amount, date }. Returns the new values:
 *   invoiceNumbers     the number appended (once - a number already listed is not repeated)
 *   invoicedAlready    the running total plus the amount, to the cent (a blank amount adds nothing)
 *   latestInvoiceDate  the later of the two dates
 * or { error } when the amount is not a number. PURE.
 * @param {{ invoiceNumbers?: string, invoicedAlready?: string | number, latestInvoiceDate?: string } | null | undefined} fields
 * @param {{ number?: string, amount?: string | number, date?: string } | null | undefined} inv
 */
export function addInvoice(fields, inv) {
  const f = fields || {};
  const number = String((inv && inv.number) || '').trim();
  const rawAmount = String((inv && inv.amount) ?? '').trim();
  const amount = rawAmount === '' ? 0 : num(rawAmount);
  if (amount === null) return { error: 'The amount must be a number.' };
  if (!number && !amount) return { error: 'Enter an invoice # or an amount.' };
  const list = String(f.invoiceNumbers || '')
    .split(/[,;/]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  const invoiceNumbers = number && !list.includes(number) ? [...list, number].join(', ') : list.join(', ');
  const total = Math.round(((num(f.invoicedAlready) || 0) + amount) * 100) / 100;
  const date = String((inv && inv.date) || '').slice(0, 10);
  const was = String(f.latestInvoiceDate || '').slice(0, 10);
  return {
    invoiceNumbers,
    invoicedAlready: amount || f.invoicedAlready !== '' ? total.toFixed(2) : '',
    latestInvoiceDate: date && date > was ? date : f.latestInvoiceDate || '',
  };
}
